/*
  impact-live.js — drives the Impact page's live behaviour:
    1. Scroll-reveal for [data-impact-reveal] and the comparison bars.
    2. The hero "bottles saved from landfills" counter.
    3. Live order toasts.

  Live data comes from a Shopify App Proxy endpoint (same-origin, no CORS),
  configured via data-endpoint on .impact-hero (live: /apps/api/impact). With a
  blank endpoint the hero shows its server-rendered base number, no "LIVE"
  indicator appears, and no toasts fire.

  "LIVE" pill: a repeat visitor gets it immediately from the localStorage cache
  of the last live total (so the counter doesn't sit on the stale base for the
  ~4s the endpoint takes). It is REMOVED again if the endpoint then fails
  LIVE_FAILURE_LIMIT times in a row, so a dead endpoint never leaves a pulsing
  LIVE badge over stale data.

  Endpoint contract (see docs/IMPACT_LIVE_ENDPOINT.md):
    GET {endpoint}                -> initial summary + recent orders (backfill: the
                                     3 newest are shown as a brief toast burst so the
                                     page always has some activity — see seedOrders)
    GET {endpoint}?since={cursor} -> summary + orders newer than cursor (these ARE toasted)
    200 application/json {
      "bottles_saved":  number,   // authoritative running total
      "as_of":          ISO-8601, // when the total was measured
      "rate_per_second": number,  // optional — enables smooth ticking between polls
      "orders": [ { "id","city","region","country_code","bottles","at" } ],
      "cursor": string            // opaque; echo back as ?since= next poll
    }
*/
(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh && r.bottom > 0;
  }

  // "1 min ago" style relative time from an ISO-8601 string (returns '' if unparseable).
  function relTime(at) {
    var ms = Date.parse(at);
    if (isNaN(ms)) return '';
    var s = Math.floor((Date.now() - ms) / 1000);
    if (s < 5) return 'just now';
    if (s < 90) return '1 min ago';
    var m = Math.round(s / 60); if (m < 60) return m + ' min ago';
    var h = Math.round(m / 60); if (h < 24) return h + (h === 1 ? ' hr ago' : ' hrs ago');
    var d = Math.round(h / 24); return d + (d === 1 ? ' day ago' : ' days ago');
  }

  // Last-known live total, cached so repeat visitors see it (and the LIVE pill)
  // instantly instead of waiting on the server-computed endpoint.
  var IMPACT_CACHE_KEY = 'eb_impact_total_v1';
  function readImpactCache() { try { return JSON.parse(localStorage.getItem(IMPACT_CACHE_KEY) || 'null'); } catch (e) { return null; } }
  function writeImpactCache(n) { try { localStorage.setItem(IMPACT_CACHE_KEY, JSON.stringify({ n: n })); } catch (e) {} }

  /* ---------------------------------------------------------------
     1. Generic scroll reveal
     CSS default is visible; we only hide (and then reveal) elements that are
     OFF-SCREEN at load. On-screen elements are left untouched (no flash), and
     if this script never runs nothing is ever hidden.
  --------------------------------------------------------------- */
  function setupReveal() {
    var els = document.querySelectorAll('[data-impact-reveal]');
    if (!els.length) return;
    // Reduced motion or no IO support → leave everything visible.
    if (prefersReducedMotion || !('IntersectionObserver' in window)) return;
    // On desktop the pin-and-scrub FX (setupScrollFx) owns the reveals; this
    // lightweight IO reveal only runs on smaller screens (no pinning there).
    if (window.matchMedia && window.matchMedia('(min-width: 990px)').matches) return;

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    els.forEach(function (el) {
      if (inViewport(el)) return; // already visible — don't hide it
      el.classList.add('impact-reveal--init');
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------
     2. Comparison chart — staggered "fill the bottle" entrance, then an
     interactive cursor-repel where dots flee the pointer and spring back.
     Plays whether the chart is below the fold (on scroll) or already on
     screen at load (after a short beat) — it now sits near the top.
  --------------------------------------------------------------- */
  function setupComparison() {
    // The comparison block now runs its own GSAP ScrollTrigger animation inline
    // in sections/impact-comparison.liquid. This legacy arm/reveal/repel path is
    // disabled so it can't fight the GSAP timeline.
    return;
    /* eslint-disable no-unreachable */
    var charts = document.querySelectorAll('.impact-compare');
    if (!charts.length) return;

    charts.forEach(function (chart) {
      var dots = chart.querySelectorAll('.impact-mass__dot');
      if (!dots.length) return;

      // Reduced motion / no IO support → no entrance + no repel (respect the
      // preference); dots just render in place via CSS.
      if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

      function reveal() {
        chart.classList.add('is-revealed');
        // After the fill-in + seal settle, hand transforms over to the repel
        // interaction: drop the animation classes (so they stop pinning each
        // dot's transform) and switch on the spring transition.
        window.setTimeout(function () {
          chart.classList.remove('impact-compare--armed');
          chart.classList.remove('is-revealed');
          chart.classList.add('impact-compare--repel');
          enableRepel(chart, dots);
        }, 2600);
      }

      chart.classList.add('impact-compare--armed');

      if (inViewport(chart)) {
        window.setTimeout(reveal, 320);
      } else {
        var io = new IntersectionObserver(
          function (entries, obs) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                reveal();
                obs.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.2 }
        );
        io.observe(chart);
      }
    });
  }

  /* Cursor-repel: dots within a radius of the pointer are pushed directly away,
     scaled by how close they are. Dot centers are measured once (relative to
     the chart, so scrolling doesn't invalidate them) and re-measured on resize.
     Work is throttled to one rAF per pointer move, and only dots inside the
     radius (plus any that need resetting) are written each frame. */
  function enableRepel(chart, dots) {
    var RADIUS = 96; // px of influence
    var PUSH = 28; // px max displacement
    var centers = [];
    var rafId = null;
    var px = 0;
    var py = 0;

    function measure() {
      var rect = chart.getBoundingClientRect();
      centers = [];
      for (var i = 0; i < dots.length; i++) {
        var r = dots[i].getBoundingClientRect();
        centers.push({
          x: r.left - rect.left + r.width / 2,
          y: r.top - rect.top + r.height / 2
        });
      }
    }

    function frame() {
      rafId = null;
      var rect = chart.getBoundingClientRect();
      var cx = px - rect.left;
      var cy = py - rect.top;
      for (var i = 0; i < dots.length; i++) {
        var c = centers[i];
        var dx = c.x - cx;
        var dy = c.y - cy;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < RADIUS && dist > 0.01) {
          var p = (1 - dist / RADIUS) * PUSH;
          dots[i].style.transform =
            'translate(' + ((dx / dist) * p).toFixed(1) + 'px,' + ((dy / dist) * p).toFixed(1) + 'px)';
        } else if (dots[i].style.transform) {
          dots[i].style.transform = '';
        }
      }
    }

    measure();
    window.addEventListener('resize', function () {
      window.requestAnimationFrame(measure);
    });

    chart.addEventListener('mousemove', function (e) {
      px = e.clientX;
      py = e.clientY;
      if (rafId == null) rafId = window.requestAnimationFrame(frame);
    });
    chart.addEventListener('mouseleave', function () {
      for (var i = 0; i < dots.length; i++) {
        if (dots[i].style.transform) dots[i].style.transform = '';
      }
    });
  }

  /* ---------------------------------------------------------------
     3. Hero live counter + toasts
  --------------------------------------------------------------- */
  function setupHero() {
    var hero = document.querySelector('.impact-hero[data-base]');
    if (!hero) return;

    var numberEl = hero.querySelector('[data-impact-number]');
    if (!numberEl) return;

    var base = parseInt(hero.getAttribute('data-base'), 10);
    if (isNaN(base) || base < 0) base = 0;

    var endpoint = (hero.getAttribute('data-endpoint') || '').trim();
    var pollSeconds = parseInt(hero.getAttribute('data-poll'), 10);
    if (isNaN(pollSeconds) || pollSeconds < 5) pollSeconds = 20;

    var fmt;
    try {
      fmt = new Intl.NumberFormat(document.documentElement.lang || undefined);
    } catch (e) {
      fmt = { format: function (n) { return String(n); } };
    }

    var state = {
      displayed: base,            // currently painted (float)
      authoritative: base,        // last server total
      asOfMs: Date.now(),         // when authoritative was measured
      ratePerSec: 0,              // optional drift rate
      live: false,                // confirmed by endpoint
      revealed: false,
      lastPainted: -1
    };

    // Repeat visit: paint the last-known live total + LIVE pill immediately rather than
    // sitting on the static base for the ~4s the endpoint takes. The poll still refreshes
    // it; ratePerSec stays 0 until then so there's no drift overshoot from a stale asOf.
    var impactCached = readImpactCache();
    if (impactCached && typeof impactCached.n === 'number' && isFinite(impactCached.n) && impactCached.n >= base) {
      state.authoritative = impactCached.n;
      state.live = true;
      hero.classList.add('is-live');
    }

    function targetNow() {
      if (state.live && state.ratePerSec > 0) {
        var elapsed = (Date.now() - state.asOfMs) / 1000;
        return state.authoritative + state.ratePerSec * elapsed;
      }
      return state.authoritative;
    }

    function paint(value) {
      var rounded = Math.floor(value);
      if (rounded === state.lastPainted) return;
      state.lastPainted = rounded;
      numberEl.textContent = fmt.format(rounded);
    }

    // rAF easing loop — eases displayed toward the (possibly drifting) target.
    // It stops itself once the number has settled with nothing drifting, and
    // whenever the hero is off-screen; applySummary() / visibility / the hero
    // coming back into view restart it. (It used to re-queue every frame forever.)
    var rafId = null;
    var heroOnScreen = true;
    function tick() {
      rafId = null;
      var target = targetNow();
      var diff = target - state.displayed;
      // snap when very close, otherwise ease
      var settled = Math.abs(diff) < 0.5;
      if (settled) {
        state.displayed = target;
      } else {
        state.displayed += diff * 0.045;
      }
      paint(state.displayed);
      var drifting = state.live && state.ratePerSec > 0;
      if (!heroOnScreen || (settled && !drifting)) return;
      rafId = window.requestAnimationFrame(tick);
    }
    function startLoop() {
      if (rafId == null) rafId = window.requestAnimationFrame(tick);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        heroOnScreen = entries[0].isIntersecting;
        if (heroOnScreen && state.revealed && !prefersReducedMotion && !document.hidden) startLoop();
      }).observe(hero);
    }
    function stopLoop() {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    // How far below the real total the counter starts on first reveal — it
    // climbs ~200 up to the live number over a couple of seconds (paired with
    // the slower easing factor in tick()) so the count-up reads as deliberate.
    var INTRO_OFFSET = 200;

    // Initial count-up reveal: start a touch below base so it visibly counts up.
    function reveal() {
      if (state.revealed) return;
      state.revealed = true;
      if (prefersReducedMotion) {
        state.displayed = state.authoritative;
        paint(state.displayed);
      } else {
        state.displayed = Math.max(0, state.authoritative - INTRO_OFFSET);
        paint(state.displayed);
        startLoop();
      }
    }

    // Observe the hero number to trigger the count-up.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              reveal();
              obs.disconnect();
            }
          });
        },
        { threshold: 0.4 }
      );
      io.observe(hero);
    } else {
      reveal();
    }

    /* ---- toasts ---- */
    var toastRegion = null;
    var toastQueue = [];
    var toastShowing = false;

    function ensureRegion() {
      if (toastRegion) return toastRegion;
      toastRegion = document.createElement('div');
      toastRegion.className = 'impact-toast-region';
      /* Not announced. The toasts rotate through live orders for as long as the
         page is open, and as a role="status" / aria-live="polite" region every
         one was read out, with no way to stop it (WCAG 2.2.2). They're ambient
         social proof, not status. aria-live="off" (and no status role) keeps
         them out of the speech queue while leaving the text in the DOM for
         anyone who browses to it. */
      toastRegion.setAttribute('aria-live', 'off');
      document.body.appendChild(toastRegion);
      return toastRegion;
    }

    function locationLabel(order) {
      var parts = [];
      if (order.city) parts.push(String(order.city));
      if (order.region) parts.push(String(order.region));
      else if (order.country_code) parts.push(String(order.country_code));
      return parts.join(', ');
    }

    function buildToast(order) {
      var n = parseInt(order.bottles, 10);
      if (isNaN(n) || n < 1) n = 1;

      var toast = document.createElement('div');
      toast.className = 'impact-toast';

      var text = document.createElement('div');
      text.className = 'impact-toast__text';

      var line1 = document.createElement('p');
      line1.className = 'impact-toast__line1';
      var loc = locationLabel(order);
      var rt = relTime(order.at) || 'just now';
      line1.textContent = loc ? rt + ' · ' + loc : rt;

      var line2 = document.createElement('p');
      line2.className = 'impact-toast__line2';
      var strong = document.createElement('strong');
      strong.textContent = fmt.format(n) + (n === 1 ? ' bottle' : ' bottles');
      line2.appendChild(strong);
      line2.appendChild(document.createTextNode(n === 1 ? ' saved from the landfill' : ' saved from landfills'));

      text.appendChild(line1);
      text.appendChild(line2);
      toast.appendChild(text);
      return toast;
    }

    function pump() {
      if (toastShowing || document.hidden) return;
      var order = toastQueue.shift();
      if (!order) return;
      toastShowing = true;

      var region = ensureRegion();
      var toast = buildToast(order);
      region.appendChild(toast);
      updateToastScheme(); // pick black-on-light vs the default light card for where it sits now

      // force reflow so the enter transition runs
      // eslint-disable-next-line no-unused-expressions
      toast.offsetHeight;
      toast.classList.add('is-in');

      window.setTimeout(function () {
        toast.classList.remove('is-in');
        toast.classList.add('is-out');
        window.setTimeout(function () {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
          toastShowing = false;
          // small gap between toasts
          window.setTimeout(pump, 600);
        }, 400);
      }, 5000);
    }

    /* ---- toast colour scheme: over LIGHT/white sections the card flips to black
       (white + grey + green text); over any non-white section it keeps the default
       light card. Runs on scroll/resize + whenever a toast appears, on all viewports. ---- */
    function toastBgIsLight(el) {
      while (el && el.nodeType === 1 && el !== document.documentElement) {
        var cs = window.getComputedStyle(el);
        // A photo/gradient background (e.g. the forest/ocean bands) is never "white".
        if (cs.backgroundImage && cs.backgroundImage !== 'none') return false;
        var m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/.exec(cs.backgroundColor || '');
        if (m) {
          var a = m[4] === undefined ? 1 : parseFloat(m[4]);
          if (a >= 0.5) {
            var lum = 0.2126 * (+m[1]) + 0.7152 * (+m[2]) + 0.0722 * (+m[3]);
            return lum >= 225; // white + cream read as light; green/dark bands do not
          }
        }
        el = el.parentElement;
      }
      return true; // no opaque colour found → page default is light
    }

    function updateToastScheme() {
      if (!toastRegion) return;
      var x = Math.max(2, window.innerWidth - 24);
      var y = Math.max(2, window.innerHeight - 28);
      // toasts are pointer-events:auto — hide them from hit-testing so elementFromPoint
      // returns the SECTION behind the corner, not the toast itself.
      var toasts = toastRegion.querySelectorAll('.impact-toast');
      var saved = [];
      for (var i = 0; i < toasts.length; i++) { saved.push(toasts[i].style.pointerEvents); toasts[i].style.pointerEvents = 'none'; }
      var behind = document.elementFromPoint(x, y);
      for (var j = 0; j < toasts.length; j++) { toasts[j].style.pointerEvents = saved[j]; }
      toastRegion.classList.toggle('impact-toast-region--on-white', toastBgIsLight(behind));
    }

    var schemeScheduled = false;
    function scheduleSchemeUpdate() {
      if (schemeScheduled) return;
      schemeScheduled = true;
      window.requestAnimationFrame(function () { schemeScheduled = false; updateToastScheme(); });
    }
    window.addEventListener('scroll', scheduleSchemeUpdate, { passive: true });
    window.addEventListener('resize', scheduleSchemeUpdate, { passive: true });

    function enqueueOrders(orders) {
      if (!Array.isArray(orders) || !orders.length) return;
      orders.forEach(function (o) {
        if (o && o.bottles != null) toastQueue.push(o);
      });
      // never let a backlog flood the screen after returning to the tab
      if (toastQueue.length > 4) toastQueue = toastQueue.slice(-4);
      pump();
    }

    // Backfill seeding: show a few of the most recent orders right away so the page
    // always has some activity, even if no NEW orders land while the visitor is here.
    function seedOrders(orders) {
      if (!Array.isArray(orders) || !orders.length) return;
      orders.slice(0, 3).forEach(function (o) { // newest-first from the feed; brief burst
        if (o && o.bottles != null) toastQueue.push(o);
      });
      pump();
    }

    /* ---- polling ---- */
    var cursor = null;
    var firstLoad = true;
    var failures = 0;
    var consecutiveFailures = 0;
    var LIVE_FAILURE_LIMIT = 3;
    var pollTimer = null;

    function applySummary(data) {
      if (typeof data.bottles_saved === 'number' && isFinite(data.bottles_saved)) {
        state.authoritative = data.bottles_saved;
        var parsed = data.as_of ? Date.parse(data.as_of) : NaN;
        state.asOfMs = isNaN(parsed) ? Date.now() : parsed;
        writeImpactCache(state.authoritative); // so the next visit starts from here
      }
      if (typeof data.rate_per_second === 'number' && data.rate_per_second >= 0) {
        state.ratePerSec = data.rate_per_second;
      }
      if (data.cursor != null) cursor = data.cursor;

      if (!state.live) {
        state.live = true;
        hero.classList.add('is-live');
      }
      // make sure the easing loop is running to reflect new target/drift
      if (state.revealed && !prefersReducedMotion) startLoop();
      else if (prefersReducedMotion) paint(targetNow());
    }

    function poll() {
      if (!endpoint || document.hidden) return;

      var url = endpoint;
      if (cursor != null) {
        url += (endpoint.indexOf('?') === -1 ? '?' : '&') + 'since=' + encodeURIComponent(cursor);
      }

      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          failures = 0;
          consecutiveFailures = 0;
          applySummary(data);
          // First response is the backfill: seed a few recent orders (don't treat them
          // as brand-new). Subsequent polls return genuinely-new orders to toast.
          if (firstLoad) seedOrders(data.orders);
          else enqueueOrders(data.orders);
          firstLoad = false;
          schedule(pollSeconds);
        })
        .catch(function () {
          failures = Math.min(failures + 1, 5);
          // Drop the LIVE claim (possibly restored from cache) once the endpoint
          // has clearly stopped answering; the next success turns it back on.
          consecutiveFailures++;
          if (consecutiveFailures >= LIVE_FAILURE_LIMIT && state.live) {
            state.live = false;
            state.ratePerSec = 0;
            hero.classList.remove('is-live');
          }
          // Keep firstLoad = true: the next *successful* response is still the
          // backfill and must not be toasted. Back off, capped.
          schedule(pollSeconds * Math.pow(2, failures));
        });
    }

    function schedule(seconds) {
      clearTimeout(pollTimer);
      pollTimer = window.setTimeout(poll, seconds * 1000);
    }

    // Pause work while the tab is hidden; re-sync immediately on return.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopLoop();
        clearTimeout(pollTimer);
      } else {
        if (state.revealed && !prefersReducedMotion) startLoop();
        if (endpoint) poll();
        pump();
      }
    });

    if (endpoint) poll();
  }

  /* ---------------------------------------------------------------
     4. Scroll reveals (desktop)
     Each impact block (except the hero + the dot graph, which run their own
     animations) fades/slides its content in as it scrolls into view. NOTE:
     hard-pinning every section broke the layout (sections overlapped), so this
     is a non-pinning reveal — no scroll-jacking, no overlap.
     Powered by GSAP ScrollTrigger (loaded page-wide in impact-hero.liquid).
  --------------------------------------------------------------- */
  function setupScrollFx() {
    if (prefersReducedMotion) return;
    if (!(window.matchMedia && window.matchMedia('(min-width: 990px)').matches)) return;

    var tries = 0;
    (function go() {
      if (!window.gsap || !window.ScrollTrigger) {
        if (tries++ > 100) return; // GSAP never loaded → content stays visible
        return void window.setTimeout(go, 60);
      }
      var gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);

      var sections = document.querySelectorAll('.impact-section');
      Array.prototype.forEach.call(sections, function (sec) {
        // Hero (own counter), the dot graph (own animation), and the accordion
        // (position:sticky cards that a transform would break) opt out.
        if (sec.classList.contains('impact-compare--graph') || sec.classList.contains('impact-hero') || sec.classList.contains('impact-accordion')) return;

        var targets = sec.querySelectorAll('[data-impact-reveal]');
        if (!targets.length) targets = sec.querySelectorAll('.impact-wrap > *');
        if (!targets.length) return;

        gsap.set(targets, { opacity: 0, y: 44 });
        gsap.to(targets, {
          opacity: 1,
          y: 0,
          ease: 'power3.out',
          duration: 0.8,
          stagger: 0.14,
          scrollTrigger: { trigger: sec, start: 'top 78%', once: true }
        });
      });
      window.ScrollTrigger.refresh();
    })();
  }

  function init() {
    setupReveal();
    setupComparison();
    setupHero();
    setupScrollFx();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
